import React, { useState, useEffect } from 'react';
import {
  workIdService,
  type WorkIdDetail,
  type WorkIdHistory,
} from '../../../services/workIdService';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { Alert, Button, Form, HistoryPanel, ListGroup, Spinner, StatusBadge } from '@/components/ui';

interface WorkIdHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onAddComment?: (updatedWorkId: WorkIdDetail) => void;
}

const WorkIdHistoryModal: React.FC<WorkIdHistoryModalProps> = ({
  visible,
  onClose,
  workId,
  onAddComment,
}) => {
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localHistory, setLocalHistory] = useState<WorkIdHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const canViewHistory = hasPermission('workId:history_view');
  const canAddComment = hasPermission('workId:history_create');

  // Fetch history when modal becomes visible
  useEffect(() => {
    const fetchHistory = async () => {
      if (visible && workId?.id) {
        try {
          setIsLoading(true);
          const history = await workIdService.getHistory(workId.id);
          setLocalHistory(history);
        } catch (error) {
          console.error('Error fetching work ID history:', error);
          setLocalHistory(workId.workIdHistory || []);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchHistory();
  }, [visible, workId?.id]);

  // Sync with workId.workIdHistory when available (e.g. from getById)
  useEffect(() => {
    if (workId?.workIdHistory && localHistory.length === 0 && !isLoading) {
      setLocalHistory(workId.workIdHistory);
    }
  }, [workId?.workIdHistory, isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser?.id || !workId) return;
    if (!canAddComment) return;

    try {
      setIsSubmitting(true);
      const userId = typeof currentUser.id === 'number' ? currentUser.id : parseInt(String(currentUser.id), 10);
      if (isNaN(userId)) {
        alert('Invalid user session. Please log in again.');
        return;
      }
      await workIdService.addHistoryComment(workId.id, {
        comments: newComment.trim(),
        userId,
      });

      // Refresh history
      const history = await workIdService.getHistory(workId.id);
      setLocalHistory(history);
      setNewComment('');

      if (onAddComment) {
        const updated = await workIdService.getById(workId.id);
        onAddComment(updated);
      }
    } catch (error: unknown) {
      console.error('Error adding comment:', error);
      const message = error instanceof Error ? error.message : 'Failed to add comment';
      alert(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedHistory = [...localHistory].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const formatHistoryComments = (history: WorkIdHistory) => {
    if (!history.comments) return null;
    const lines = history.comments.split('\n');
    if (lines.length > 1) {
      return (
        <div className="tw-mb-1">
          {lines.map((line: string, idx: number) => (
            <div key={idx} className={idx === 0 ? 'tw-font-semibold' : ''}>
              {line}
            </div>
          ))}
        </div>
      );
    }
    return <p className="tw-mb-1">{history.comments}</p>;
  };

  const formatStageLabel = (stage: string) => {
    const labels: Record<string, string> = {
      manual_comment: 'Manual Comment',
      created: 'Created',
      reported: 'Reported',
      closed: 'Closed',
      closed_without_action: 'Closed without action',
      reopened: 'Reopened',
      status_change: 'Status Change',
      approval: 'Approval',
      assignment: 'Assignment',
      cancellation: 'Cancellation',
      merged: 'Merged',
      unmerged: 'Unmerged',
      data_edit: 'Data Edit',
    };
    return labels[stage] || stage;
  };

  const getHistoryActorLabel = (history: WorkIdHistory): string => {
    // For created/reported entries, prefer the Work ID's reportedBy user when available
    if (['created', 'reported'].includes(history.stage) && workId?.reportedBy) {
      return `By ${workId.reportedBy.name} ${workId.reportedBy.lastName}`;
    }

    if (history.user) {
      return `By ${history.user.name} ${history.user.lastName}`;
    }

    return 'By System';
  };

  if (!canViewHistory) {
    return null;
  }

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title="Work ID History"
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <HistoryPanel
        composer={
          canAddComment ? (
            <form onSubmit={handleSubmit}>
              <div className="tw-mb-3">
                <Form.Label>Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  // Shorter on a phone, where the composer sits above the timeline and a
                  // six-row textarea would push the history off screen on its own.
                  rows={3}
                  placeholder="Enter your comment here..."
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                className="tw-w-full"
                disabled={!newComment.trim() || isSubmitting}
              >
                {isSubmitting ? 'Adding...' : 'Add Comment'}
              </Button>
            </form>
          ) : (
            <Alert variant="warning">
              <i className="bi bi-exclamation-triangle tw-mr-2"></i>
              You don&apos;t have permission to add comments to work ID history.
            </Alert>
          )
        }
      >
        {isLoading ? (
          <div className="tw-text-center tw-py-4">
            <Spinner animation="border" variant="primary">Loading...</Spinner>
          </div>
        ) : sortedHistory.length > 0 ? (
          <ListGroup>
            {sortedHistory.map((history) => (
              <ListGroup.Item key={history.id}>
                <div className="tw-flex tw-justify-between tw-items-start">
                  <div className="tw-grow">
                    <h6 className="tw-mb-1">
                      {history.summary || formatStageLabel(history.stage)}
                    </h6>
                    <p className="tw-mb-1 tw-text-content-muted">
                      Stage:{' '}
                      <StatusBadge status={history.stage} label={formatStageLabel(history.stage)} />
                    </p>
                    {formatHistoryComments(history)}
                    <small className="tw-text-content-muted">{getHistoryActorLabel(history)}</small>
                  </div>
                  <small className="tw-text-content-muted tw-ml-2">
                    {new Date(history.createdAt).toLocaleString()}
                  </small>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <Alert variant="info">No history records found.</Alert>
        )}
      </HistoryPanel>
    </ResponsiveModal>
  );
};

export default WorkIdHistoryModal;
